import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import { SectionEyebrow, RevealContainer, fadeIn } from '@/components/marketing/tactile';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface FAQItem {
    q: string;
    a: string;
}

/**
 * Convert FAQ items to the shape expected by `faqPageSchema` in
 * lib/structured-data, so pages can pass FAQ structured data through SEOHead's
 * `structuredData` prop (the codebase's safe JSON-LD injection path) rather than
 * inlining a script tag here.
 */
export function faqItemsToSchemaInput(items: FAQItem[]): Array<{ question: string; answer: string }> {
    return items.map((it) => ({ question: it.q, answer: it.a }));
}

/**
 * Objection-handling FAQ accordion. Presentational only — feed the same `items`
 * to `faqPageSchema(faqItemsToSchemaInput(items))` via SEOHead for structured
 * data. Answers must be honesty-reviewed (mechanism-based, no unverifiable claims).
 */
export function ObjectionFAQ({
    items,
    eyebrow = 'Questions buyers ask',
    title = 'The questions a partner actually asks.',
    className,
}: {
    items: FAQItem[];
    eyebrow?: string;
    title?: string;
    className?: string;
}) {
    return (
        <section className={cn('border-t border-zinc-800/40 px-6 py-28', className)}>
            <RevealContainer className="mx-auto max-w-3xl">
                <motion.div variants={fadeIn} className="mb-12 text-center">
                    <SectionEyebrow>{eyebrow}</SectionEyebrow>
                    <h2 className="landing-display-lg text-white">{title}</h2>
                </motion.div>
                <motion.div variants={fadeIn}>
                    <Accordion type="single" collapsible className="w-full">
                        {items.map((it, i) => (
                            <AccordionItem
                                key={i}
                                value={`faq-${i}`}
                                className="border-b border-white/10"
                            >
                                <AccordionTrigger className="font-sans-swiss text-left text-[16px] font-medium text-white hover:text-[#A9C2B1] hover:no-underline">
                                    {it.q}
                                </AccordionTrigger>
                                <AccordionContent className="landing-body-md text-[#CBD5E1]/80">
                                    {it.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </motion.div>
            </RevealContainer>
        </section>
    );
}
